import { AnimatePresence } from 'framer-motion';
import { FC, useCallback, useState } from 'react';

import { Modal, ModalContent, ModalOverlay } from '@bonusly/design-system';
import { useUser } from '@bonusly/user-queries';

import { CalendarIntegration } from '../calendar-integration';
import { CreateMeetingContent } from './create-meeting-content';

interface CreateMeetingModalProps {
	guestId: string;
	isOpen: boolean;
	onClose: VoidFunction;
}

enum STEPS {
	CALENDAR_CONNECT = 'calendar-connect',
	MANUAL = 'manual',
}

export const CreateMeetingModal: FC<CreateMeetingModalProps> = ({ guestId, isOpen = false, onClose }) => {
	const { data: guest } = useUser(guestId);
	const [step, setStep] = useState(STEPS.CALENDAR_CONNECT);

	const setManualStep = useCallback(() => {
		setStep(STEPS.MANUAL);
	}, []);

	const setCalendarConnectStep = useCallback(() => {
		setStep(STEPS.CALENDAR_CONNECT);
	}, []);

	const onCloseModal = useCallback(() => {
		setCalendarConnectStep();
		onClose();
	}, [onClose, setCalendarConnectStep]);

	if (!guest) {
		return null;
	}

	return (
		<>
			<Modal autoFocus={false} closeOnOverlayClick={false} isOpen={isOpen} onClose={onCloseModal} scrollBehavior="outside" size="md" isCentered>
				<ModalOverlay />
				{/* 
					TOUR: Something to keep in mind with modals is that the <ModalContent> (which is part of the below compoent) is lazy-loaded
					Any hook calls you make up there ^ are going to be there all the time, whether or not the modal is open or closed.
					The *content* of the modal, however, does not exist until the modal opens. So, in this case we are always loading the `guest` user
					whenever this component is rendered. But, any hooks inside the modal content will only be called when the modal is open.
					This is important to keep in mind when Modals or Popovers include network calls. Here we're only making a single call to get the guest
					and there's a high chance that the data is already in the cache, so it's not risky. But, if you had dozens of modals on the page
					you might want to adjust the logic and move any request-making hooks into the content of the modal.
				*/}

				<ModalContent tabIndex={0}>
					<AnimatePresence mode="wait">
						{step === STEPS.MANUAL && <CreateMeetingContent guest={guest} onClose={onCloseModal} openLinkCalendar={setCalendarConnectStep} />}

						{step === STEPS.CALENDAR_CONNECT && <CalendarIntegration guestId={guest.id} onManual={setManualStep} onClose={onCloseModal} />}
					</AnimatePresence>
				</ModalContent>
			</Modal>
		</>
	);
};

/*
TOUR 2: As mentioned before, this package used to have just a single <MeetingModal> component that could be used to create or edit a meeting.
This proved to be a bit unwieldy - the component was trying to do too much. There were conditionals and safe navigation checks all over the place to handle
whether or not an Event was being created or edited - in the latter case we had a record to work with, in the former we did not.

To handle this, I split the component into two separate components - <CreateMeetingModal> and <EditMeetingModal>. This way each component can be more focused
on its specific use case and there is less conditional logic to worry about across the board.

I do want to note that there are still shared components between the two! These top level components are responsible really for just fetching the data that's
needed, but once it's there the "guts" of the modals look very similar. Still, whenever it made sense for the modal to *behave* differently based on whether
it was creating or editing a meeting, I split that logic apart and placed it in the respective component.

*Prop Changes*

Another change that was made here was to simply accept a `guestId` prop instead of a `guest` prop. The `guest` prop required us to build a structured
JSON object and pass it in. Depdending on where this modal was being used we might have had data in the correct format, or maybe not. In that case we'd have to
transform the data before passing it in. This was a bit of a pain and made the component less flexible. You can see this in action where we had to build a very
specific object in the `Growth` app: https://gitlab.com/bonusly/engineering/front-end/kaleidoscope/-/blob/e9b53010a0e2dd2a9ccf8f030790a6b278f0aa97/apps/growth/src/components/meeting/current-one-on-one.tsx#L35

Instead, we can just pass in the `guestId` and let the component handle the fetching of the data. There *are* drawbacks to this - we may potentially make an
extra network request that we didn't need to make. But, as me move to a more global API cache, this will be less of a concern because we could pull the needed
data from the cache if it's already been fetched.

So, with these changes in place this component is really just there to gather the necessary data and pass it down to the content for rendering.

While we're talking about global API stuff, let's look at the `Event` entity, which is the data we're working with here.
Head over to `packages/api/entities/one-on-ones/src/lib/schema.ts` and I'll meet you there!

*/
